import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, map, startWith, timer } from 'rxjs';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { CompanyFilterItem, CompanyFilterService } from 'src/app/services/filters/company-filter.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { NotificationQueries, SharedQueries } from 'src/app/shared/types/get-query';
import { NotificationUser } from 'src/app/shared/types/notification-user.type';
import { PageInfo } from 'src/app/shared/types/page-info.type';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { AuthService } from '../../auth/auth.service';
import { Context, User, UserProfile } from '../../models/context.model';
import { Utilities } from '../../providers/utilities';
import { ContextService } from '../../services/context.service';
import { UiAvatarImageComponent } from '../ui-avatar/ui-avatar-image.component';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';

type Company = {
	id: number;
	name: string;
};

@Component({
	selector: 'up-top-header',
	templateUrl: './up-top-header.component.html',
	styleUrls: ['./up-top-header.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		UiAvatarImageComponent,
		MatIconModule,
		MatBadgeModule,
		MatInputModule,
		MatAutocompleteModule,
		SharedModule
	]
})
export class UpTopHeaderComponent implements OnInit {
	readonly #destroyRef = inject(DestroyRef);
	readonly #graphQLService = inject(GraphQLService);
	readonly #router = inject(Router);
	readonly #dialog = inject(MatDialog);
	readonly #companyFilterService = inject(CompanyFilterService);

	dashboard: Array<any> = [];
	user: User = new User();
	profiles: Array<UserProfile> = [];
	platformId = 0;
	employeeBasicNetPlatformId = 3;
	notificationBadgeAmount: number = 0;
	notifications: NotificationUser[] = [];
	notificationPageInfo: PageInfo = { hasPreviusPage: false, hasNextPage: false, startCursor: null, endCursor: null };
	loadingNotifications = false;

	get notificationBadgeLabel(): string | number {
		return this.notificationBadgeAmount > 9 ? '9+' : this.notificationBadgeAmount;
	}

	showApps = false;
	showUser = false;
	showNotifications = false;

	companyNameControl = new FormControl<Company | string | null>(null);
	companies: Array<Company> = [];
	pageInfo: PageInfo = { hasPreviusPage: false, hasNextPage: false, startCursor: null, endCursor: null };
	totalElements: number = 0;

	environment = {
		loginUrl: '',
		logoffUrl: '',
		landingUrl: '',
		baseSSOUrl: '',
		basicNetBaseUrl: '',
		basicAppBaseUrl: '',
		baseResourceUrl: '',
		isAuthenticated: true
	};

	landingUrl = '/';

	//se il token è popolato variabile popoalta altrimenti undefined
	isLogged: any;

	compactMode: boolean = false;

	get selectedCompanyName(): string {
		const selectedCompany = this.companyNameControl.value;
		return typeof selectedCompany === 'string' ? selectedCompany || 'Select company' : selectedCompany?.name || 'Select company';
	}

	constructor(
		private contextService: ContextService,
		private httpClient: HttpClient,
		public elem: ElementRef,
		private authService: AuthService,
		// private notificationsService: NotificationsService,
		// private compactModeService: CompactModeService
	) {
		this.companyNameControl.valueChanges
			.pipe(takeUntilDestroyed(), startWith(''), debounceTime(300), filter((value) => typeof value === 'string'))
			.subscribe((value: string) => {
				this.getCompanies(value, true);
			});
	}

	public ngOnInit() {
		this.setEnvironmentParams();
		this.authService.getTokenObservable.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((res) => {
			this.isLogged = res;
			if (res) {
				this.resolveSelectedCompany();
				const search = typeof this.companyNameControl.value === 'string' ? this.companyNameControl.value : '';
				this.getCompanies(search, true);
			} else {
				this.companies = [];
				this.companyNameControl.setValue(null, { emitEvent: false });
				this.notificationBadgeAmount = 0;
			}
		});

		timer(0, 30000)
			.pipe(
				filter(() => !!this.authService.getTokenValue),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe(() => this.getMyUnreadNotificationCount());

		// Sottoscrizione alle notifiche per aggiornare il numero nel badge
		// this.notificationsService.broadcastNotifications().subscribe((n) => (this.notificationBadgeAmount = this.notificationsService.getNotificationsTotalCount()));

		this.contextService
			.getObservableContext()
			.pipe(filter((context) => !!context), takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (context: Context) => {
					this.dashboard = this.manageDashboard(context?.Current?.Page?.Header?.Dashboard?.Links ?? []);
					this.user = context.Current.User;
					this.profiles = context.Current.User.UserProfiles ?? [];
					//console.warn('platform id da gestire');
					this.platformId = 3;
					this.landingUrl = context.Settings.AppPaths.Local?.Base ?? '/';
					if (this.user && this.profiles) {
						this.landingUrl = this.profiles.find((p) => p.Id === this.user.CurrentProfileId)?.LandingUrl ?? '/';
						this.environment.landingUrl = this.landingUrl;
					}
				},
				error: (err: any) => {
					console.error(err);
				}
			});
	}

	displayCompany(company: Company): string {
		return company?.name || '';
	}

	inViewportAction(ev: { isVisible: boolean }, item: Company) {
		if (!ev.isVisible) return;
		const index = this.companies.findIndex((el) => el === item);
		if (index === this.companies.length - 1 && this.pageInfo.hasNextPage) {
			this.getCompanies(this.companyNameControl.value as string, false);
		}
	}

	onNotificationInViewport(ev: { isVisible: boolean }, item: NotificationUser) {
		if (!ev.isVisible) return;
		const index = this.notifications.findIndex((el) => el === item);
		if (index === this.notifications.length - 1 && this.notificationPageInfo.hasNextPage) {
			this.getMyNotifications(false);
		}
	}

	openCompanyFilterModal(): void {
		const current = this.companyNameControl.value;
		const selected: Array<Selectable<CompanyFilterItem>> = typeof current === 'string' || !current
			? []
			: [
				{
					id: current.id,
					name: current.name,
					displayId: current.id,
					displayName: current.name,
					selected: true,
					disabled: false
				}
			];

		this.#dialog
			.open(FilterListComponent<CompanyFilterItem>, {
				data: {
					title: 'Select company',
					selected,
					filterService: this.#companyFilterService,
					singleSelection: true
				}
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((selectedCompanies) => {
				const pickedCompany = selectedCompanies?.[0];
				if (!pickedCompany) return;

				this.companyNameControl.setValue(
					{
						id: Number(pickedCompany.displayId),
						name: pickedCompany.displayName ?? ''
					},
					{ emitEvent: false }
				);
				this.companySelection();
			});
	}

	onCompanyOptionSelected(event: MatAutocompleteSelectedEvent): void {
		this.companyNameControl.setValue(event.option.value);
		this.companySelection();
	}

	companySelection(): void {
		const selected = this.companyNameControl.value;
		if (!selected || typeof selected === 'string') return;

		const companyId = selected.id;
		if (companyId === this.getCurrentCompanyId()) return;

		this.authService
			.authentication(this.#router.routerState.snapshot, companyId)
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe(() => {
				this.authService.setCompanySelected(companyId);

				const target = this.authService.isCompany() ? '/company' : this.authService.isPhotographer() ? '/photographer' : '/';
				// Remount feature shell so guards/sidenav pick up roles from the new token
				this.#router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
					this.#router.navigateByUrl(target);
				});
			});
	}

	public login() {
		this.authService.login();
	}

	public logoff() {
		this.authService.logout();
	}

	public changeProfile(profile: UserProfile) {
		const baseAccountsUrl = this.contextService.context.Settings.AppPaths.Core?.Base;
		const fullUrl = baseAccountsUrl + '/Account/BrowseAs';

		const obj = {
			browseId: profile.Company?.GroupId + '',
			profileId: profile.Id + '',
			clientId: this.contextService.context.Current.User.ClientId + ''
		};

		// Cancella il cookie COMPANY_SELECTED
		this.authService.clearCompanySelected();

		this.httpClient
			.get(Utilities.CleanUrl(fullUrl), { params: obj, withCredentials: true })
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe(() => (location.href = location.href = profile.LandingUrl ?? location.href));
	}

	public goToBasicPersonal() {
		location.href = this.environment.basicNetBaseUrl + '/Home/Personal';
	}

	private getCompanies(search: string, isFirst?: boolean): void {
		if (!this.authService.getTokenValue) return;

		const variables = { search: search, first: 10, after: isFirst ? null : this.pageInfo.endCursor };
		this.#graphQLService
			.getData(SharedQueries.GET_COMPANIES, variables)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['companiesByUserProfile'])
			)
			.subscribe((res) => {
				this.companies = isFirst ? res.nodes : [...this.companies, ...res.nodes];
				this.pageInfo = res.pageInfo;
				this.totalElements = res.totalCount;
			});
	}

	private getMyUnreadNotificationCount(): void {
		if (!this.authService.getTokenValue) return;

		this.#graphQLService
			.getData(NotificationQueries.GET_MY_UNREAD_NOTIFICATIONS)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['myUnreadNotificationCount'])
			)
			.subscribe((res) => {
				this.notificationBadgeAmount = res;
			});
	}

	private getMyNotifications(isFirst?: boolean): void {
		if (!this.authService.getTokenValue) return;
		if (this.loadingNotifications && !isFirst) return;

		this.loadingNotifications = true;
		const variables = { first: 8, after: isFirst ? null : this.notificationPageInfo.endCursor };
		this.#graphQLService
			.getData(NotificationQueries.GET_MY_NOTIFICATIONS, variables)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['myNotifications'])
			)
			.subscribe({
				next: (res) => {
					const nodes = res?.nodes ?? [];
					this.notifications = isFirst ? nodes : [...this.notifications, ...nodes];
					this.notificationPageInfo = res?.pageInfo ?? this.notificationPageInfo;
					this.loadingNotifications = false;
				},
				error: () => {
					if (isFirst) {
						this.notifications = [];
					}
					this.loadingNotifications = false;
				}
			});
	}

	private resolveSelectedCompany(): void {
		const companyId = this.getCurrentCompanyId();
		if (!companyId) return;

		const current = this.companyNameControl.value;
		if (typeof current !== 'string' && current?.id === companyId) return;

		this.#graphQLService
			.getData(SharedQueries.GET_COMPANY_BY_ID, { id: companyId })
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res?.data?.companies?.nodes?.[0] as Company | undefined)
			)
			.subscribe((company) => {
				if (company) {
					this.companyNameControl.setValue(company, { emitEvent: false });
				}
			});
	}

	private getCurrentCompanyId(): number | null {
		const browseCompanyId = this.authService.getDecodedAccessToken()?.BrowseCompanyId;
		if (browseCompanyId) {
			return Number(browseCompanyId);
		}

		const companySelected = document.cookie.split(';').find((cookie) => cookie.trim().startsWith('COMPANY_SELECTED='));
		if (companySelected) {
			const id = Number(companySelected.split('=')[1]);
			return Number.isFinite(id) ? id : null;
		}

		return null;
	}

	private setEnvironmentParams() {
		this.environment.baseResourceUrl = this.contextService.context.Settings.AppPaths.Core?.Api + '/resources';
		this.environment.landingUrl = this.landingUrl;
		this.environment.loginUrl = this.contextService.context.Settings.LoginUrl ?? '/';
		this.environment.logoffUrl = this.contextService.context.Settings.LogoffUrl ?? '/';
		this.environment.basicAppBaseUrl = this.contextService.context.Settings.AppPaths.BasicApp?.Base ?? '/';
		this.environment.basicNetBaseUrl = this.contextService.context.Settings.AppPaths.BasicNet?.Base ?? '/';
		this.environment.baseSSOUrl = this.contextService.context.Settings.AppPaths.BasicNet?.Base ?? '/';
	}

	private manageDashboard(links: any[]): any[] {
		const linkMap = new Map<string, Array<any>>();

		links.forEach((link) => {
			if (!linkMap.has(link.ParentMenuId)) {
				linkMap.set(link.ParentMenuId, [link]);
			} else {
				linkMap.get(link.ParentMenuId)?.push(link);
			}
		});

		return Array.from(linkMap.values()); // return Object.values(groupBy(links, l => l.ParentMenuId));
	}

	togglenotificationsSidebar() {
		this.showNotifications = !this.showNotifications;
		if (this.showNotifications) {
			this.showUser = false;
			this.showApps = false;
			this.getMyNotifications(true);
		}
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent) {
		const target = event.target as HTMLElement | null;
		if (this.showNotifications && !target?.closest('.notificationSidebarClickNuller')) {
			this.showNotifications = false;
		}
	}

	outsideUserTab(e: any) {
		if (!e.target?.className?.includes('userTabClickNuller')) {
			if (this.showUser) this.showUser = false;
		}
	}

	toggleCompactMode() {
		// this.compactModeService.toggleCompactMode();
	}

	readNotification(notification: NotificationUser): void {
		this.#graphQLService.postData(MutationQuery.MARK_NOTIFICATION_AS_READ, { markNotificationAsReadInput: { notificationUserId: notification.id } })
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe(() => {
				if (notification.notification.detailUrl) this.#router.navigateByUrl(notification.notification.detailUrl);
				this.getMyUnreadNotificationCount();
			});
	}
}
